import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { type StepDeckItem, StepRail } from "./gtm-step-deck";
import { C, FM, FS, FU, T, W } from "./tokens";

/* ────────────────────────────────────────────────────────────────────────────
   IntelSectionNav — the WITHIN-section panel stepper for the Intelligence
   "Global overview" sections (1.1–1.5), built to match the Go-to-Market command
   bar exactly (`.gtd-head-slim` / `.gtmc-head-slim`).

   The idiom, identical to GTM:
     • Row 1 of the sticky bar is a MONO kicker — the section index + short label
       ("1.1 · Global demand"), the twin of GTM's "3.2 Distribution Playbook".
       No serif title, no provenance chip, no fake right-hand CTA.
     • Row 2 is the full-width StepRail (the section's panels).
     • BELOW the bar sits the editorial header — a serif title + one-line context
       lede — and it is PER-PANEL: it names and describes whichever tab is
       selected (Headline vs Demand trends, the map vs its rankings…), swapping
       as you step the rail. This is the exact GTM shape (kicker in the bar →
       StepHead title+lede below), so the reader always has a header + context
       for the current view, not body copy hanging off a bare subtitle.

   Auto-hide: the bar slides up out of view on scroll-down and returns on the
   slightest scroll-up (never "lost"), always shown near the very top.
   ──────────────────────────────────────────────────────────────────────────── */

export function IntelSectionNav({
  steps,
  active,
  onSelect,
  n,
  kicker,
  head,
  lede,
  accent,
  actions,
  headActions,
}: {
  /** This section's panels, walked one at a time. */
  steps: StepDeckItem[];
  active: number;
  onSelect: (i: number) => void;
  /** Section number, e.g. "1.2" — the index leading the mono kicker. */
  n: string;
  /** Short section label for the kicker (e.g. "Global demand"), mirroring the
   *  left-tree name. Kept concise: it's a mono eyebrow, not the title. */
  kicker: string;
  /** The SELECTED panel's editorial title (may carry the italic accent word),
   *  rendered as the serif header directly below the bar — swaps with the tab. */
  head: ReactNode;
  /** The selected panel's one-line context copy, below its header. May carry an
   *  italic emphasis, so it's a node, not a bare string. */
  lede: ReactNode;
  /** Workspace identity hue for the command bar (kicker + rail chip + band glow).
   *  Defaults to Intelligence blue; Prioritization passes its purple. */
  accent?: string;
  /** Optional right-aligned controls on the kicker row (e.g. a year toggle),
   *  occupying the 36px GTM-parity control slot. */
  actions?: ReactNode;
  /** Optional right-aligned controls on the SERIF TITLE's row, for a control
   *  that only applies to the selected panel and reads as part of its header. */
  headActions?: ReactNode;
}) {
  // Auto-hide the command bar on scroll-down; reveal it on the slightest
  // scroll-up (the GTM 4.1 / Segmentation idiom, ported verbatim). The
  // dashboard scrolls the window (no inner scroller), so read window.scrollY
  // directly; rAF-throttled with a small jitter deadband; always shown near top.
  const [barHidden, setBarHidden] = useState(false);
  // The bar's dark protective fill is driven off scroll distance (written to
  // --bar-y on the bar element): at the scroll origin it stays fully
  // transparent so the page background shows through unbroken — no top edge, no
  // bottom edge, no plate. The opaque ramp + blur only materialise once content
  // scrolls under the bar (where masking it matters). Written imperatively via
  // ref (no re-render); the same listener that computes the auto-hide.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const readY = () => window.scrollY || document.documentElement.scrollTop || 0;
    let lastY = readY();
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = readY();
        barRef.current?.style.setProperty("--bar-y", String(y));
        if (y < 96) setBarHidden(false);
        else if (y > lastY + 6) setBarHidden(true);
        else if (y < lastY - 6) setBarHidden(false);
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div
        ref={barRef}
        className={`intel-topbar src-enter ${barHidden ? "hidden" : ""}`}
        style={
          {
            position: "sticky",
            top: 0,
            zIndex: 30,
            "--d": 0,
            ...(accent ? { "--intel-accent": accent, "--rail-accent": accent } : {}),
          } as CSSProperties
        }
      >
        <div className="intel-topbar-row">
          <span className="intel-topbar-kick">
            <span className="intel-topbar-num">{n}</span>
            {kicker}
          </span>
          {actions ? <div className="intel-topbar-actions">{actions}</div> : null}
        </div>
        {steps.length > 1 ? (
          <div className="intel-topbar-rail">
            <StepRail steps={steps} active={active} onSelect={onSelect} />
          </div>
        ) : null}
        <style>{INTEL_TOPNAV_STYLES}</style>
      </div>
      <div className="intel-sec-head src-enter" style={{ "--d": 1 } as CSSProperties}>
        <div className="intel-sec-headrow">
          <h2 className="intel-sec-title">{head}</h2>
          {headActions ? <div className="intel-sec-headactions">{headActions}</div> : null}
        </div>
        <p className="intel-sec-lede">{lede}</p>
      </div>
    </>
  );
}

const INTEL_TOPNAV_STYLES = `
  /* ── Two-row sticky command bar. A FULL-BLEED, strokeless gradient band: it
        spans the whole content width (negative gutters cancel .dash-main's
        horizontal padding, matched by inner padding so the rows realign with the
        content column) and dissolves into the page below with a soft fade — no
        border, no radius, no box-shadow, no hard boundary. The band is a
        ::before layer so the rows sit above it; positioned sticky inline (inline
        specificity beats the shell's z-index:1 rule on direct children).
        Auto-hides on scroll-down / reveals on scroll-up via the .hidden
        transform. ── */
  .intel-topbar {
    --bleed: clamp(20px, 4vw, 56px);
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin: 0 calc(-1 * var(--bleed)) 22px;
    padding: 14px var(--bleed) 16px;
    transition: transform 300ms cubic-bezier(.22,.61,.36,1);
    will-change: transform;
  }
  .intel-topbar.hidden { transform: translateY(-128%); }
  @media (prefers-reduced-motion: reduce) { .intel-topbar { transition: none; } }
  /* The band itself — the observatory "navy flair" (Intelligence blue + teal
     glows over a deep navy ramp), extended ~34px BELOW the bar and faded to
     transparent there so there is no hard bottom edge; it melts into the page.
     The linear base is SOLID hex up top so content scrolling under the sticky
     bar cannot ghost through; the backdrop blur softens whatever shows in the
     dissolve. No border / radius / shadow anywhere. */
  .intel-topbar::before {
    content: "";
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: -34px;
    background:
      radial-gradient(120% 150% at 90% -60%, color-mix(in srgb, var(--intel-accent, ${C.blue}) 20%, transparent), transparent 60%),
      radial-gradient(90% 130% at 4% -40%, rgba(38,197,173,0.11), transparent 55%),
      linear-gradient(to top, #0a1330 0, #0d1a3c 62%, #0a1430 100%);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    -webkit-mask-image: linear-gradient(to top, transparent 0, #000 34px);
    mask-image: linear-gradient(to top, transparent 0, #000 34px);
    /* Seamless at rest: the whole fill (ramp + glows + blur) is transparent at
       the scroll origin and ramps to solid over the first ~44px of scroll, so
       the band only becomes a visible surface once it has content to protect.
       --bar-y is the live scroll distance written by the component. */
    opacity: clamp(0, calc(var(--bar-y, 0) / 44), 1);
    transition: opacity 140ms linear;
  }
  /* Row 1 — the mono kicker (section index + short label), the exact twin of
     GTM's .gtd-slim-kick. Left-aligned; no provenance chip, no fake CTA. */
  .intel-topbar-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    /* Shared band row-1 height across every workspace: this row is a bare mono
       kicker, but it reserves the same 36px that GTM's control row occupies so
       both bands are the same height and the title lands at the same Y. */
    min-height: 36px;
  }
  .intel-topbar-kick {
    font-family: ${FM};
    font-size: 10.5px;
    font-weight: 400;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    /* Workspace identity hue — Intelligence blue by default, overridden inline
       (e.g. Prioritization purple) via --intel-accent. */
    color: var(--intel-accent, ${C.blue});
    line-height: 1;
    white-space: nowrap;
  }
  /* Right-aligned control slot on the kicker row (year toggle, etc.) — sits in
     the 36px GTM-parity band so a section's working controls share the bar. */
  .intel-topbar-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  /* Phone: a section's control cluster (e.g. Demand Signals' Period 3M–5Y +
     By-size toggles) is far wider than a ~343px content column and, kept on the
     kicker's line with margin-left:auto, it can't shrink — so it overran the
     viewport and dragged the fixed top bar off-screen. Drop it onto its own
     full-width line below the kicker and let its controls wrap. Below 900px only
     the MobileShell renders, so desktop keeps the right-aligned inline slot. */
  @media (max-width: 899px) {
    .intel-topbar-actions {
      margin-left: 0;
      width: 100%;
      min-width: 0;
      /* A single segmented control (Period 3M–5Y) is itself wider than the phone
         column, so wrapping can't save it — put the cluster on one line that
         swipes horizontally instead of being clipped. */
      flex-wrap: nowrap;
      overflow-x: auto;
      justify-content: flex-start;
      /* room so the scrollbar/last chip doesn't crowd the kicker above */
      padding-bottom: 2px;
    }
  }
  /* Section index leading the kicker — same mono eyebrow, spaced off the label. */
  .intel-topbar-num { margin-right: 8px; }
  /* Row 2 — the segmented rail spans the whole width (equal segments, no cap).
     No rule between the rows: they are one band, and the rail below already
     draws its own bordered track — a hairline directly above it only stacked a
     second horizontal line 9px from the first. The gap separates them. */
  .intel-topbar-rail {
    padding-top: 9px;
  }
  .intel-topbar-rail .gsd-rail { width: 100%; max-width: none; }
  /* ── Below-bar editorial header — PER-PANEL. Serif title (the agreed section
        header type: FS · ${T.title}px · W.light · line-height 1.2) + a one-line
        context lede, both describing the selected tab. This is the GTM StepHead
        shape: the bar names the section, the header names the view. ── */
  .intel-sec-head { margin: 0 2px 26px; }
  /* Title row: the serif header, with an optional control cluster pushed to the
     right edge and sitting on the title's baseline. Wraps onto its own line
     before the title is squeezed. */
  .intel-sec-headrow {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .intel-sec-headactions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;
    /* Optical: sit the control on the title's baseline rather than its box. */
    padding-bottom: 3px;
  }
  .intel-sec-title {
    font-family: ${FS};
    font-weight: ${W.light};
    font-size: 30px;
    color: ${C.textHi};
    line-height: 1.2;
    letter-spacing: 0;
    margin: 0;
  }
  .intel-sec-lede {
    font-family: ${FU};
    font-size: ${T.lead}px;
    font-weight: ${W.body};
    line-height: 1.55;
    color: ${C.textMd};
    margin: 10px 0 0;
  }

  /* ── Light-mode mirror (theme toggle) — the navy flair band becomes a LIGHT
        band: the two identity glows (accent blue + teal) are kept exactly as the
        dark band, only the deep-navy linear ramp swaps for a light ramp, so when
        the bar materialises on scroll it protects the content beneath with a
        light surface instead of dropping a dark slab onto the light page. The
        bright kicker darkens to a light-legible version of the SECTION'S OWN
        accent (--intel-accent mixed toward navy). Title/lede already flip via C.*.
        Dark stays byte-identical — these only apply under [data-theme="light"]. */
  [data-theme="light"] .intel-topbar::before {
    background:
      radial-gradient(120% 150% at 90% -60%, color-mix(in srgb, var(--intel-accent, ${C.blue}) 20%, transparent), transparent 60%),
      radial-gradient(90% 130% at 4% -40%, rgba(38, 197, 173, 0.11), transparent 55%),
      linear-gradient(to top, #f4f7fc 0, #eef3fc 62%, #f4f7fc 100%);
  }
  /* Darken the section's OWN accent for light. This was hardcoded to Intelligence
     blue (#2b5fb0), which turned EVERY non-Intelligence section's kicker blue on
     light — the dark band reads --intel-accent (per workspace), so light must too,
     or the workspace loses its identity colour the moment you flip the theme. Mix
     whatever accent the section passed toward navy: blue→dark blue, coral→dark
     coral, violet→dark violet. Dark keeps the raw var above (byte-identical). */
  [data-theme="light"] .intel-topbar-kick,
  [data-theme="light"] .intel-topbar-num {
    color: color-mix(in oklab, var(--intel-accent, ${C.blue}), #000f47 45%);
  }
`;
